import React, { FC, memo, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ICommonComponentProps } from '@/types';
import { Dropdown, Input, Menu } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import { Timezone, getAllTimezones, getCountry } from 'countries-and-timezones';
import clsx from 'clsx';

export type TAugmentedTimezone = Timezone & {
  countryNames: string[];
};

const getAllAugmentedTimezones = (): TAugmentedTimezone[] => {
  return Object.values(getAllTimezones()).map((tz) => ({
    ...tz,
    countryNames: tz.countries.map((countryCode) => getCountry(countryCode).name),
  }));
};

interface ITimezoneDropdownProps extends ICommonComponentProps {
  onSelect?: (name: string) => void;
  disabled?: boolean;
  errors?: boolean;
  value?: string;
}

export const TimezoneDropdown: FC<ITimezoneDropdownProps> = memo(
  ({ onSelect, disabled, errors, value, className }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState<string>('');

    const allTimezones = useMemo(() => getAllAugmentedTimezones(), []);
    const timezonesShowed = useMemo(() => {
      if (!searchText) {
        return allTimezones;
      }

      const searchLower = searchText.toLowerCase();
      return allTimezones.filter((tz) => {
        if (tz.name.toLowerCase().includes(searchLower)) {
          return true;
        }

        if (tz.countryNames.some((country) => country.toLowerCase().includes(searchLower))) {
          return true;
        }

        if (tz.utcOffsetStr.toLowerCase().includes(searchLower)) {
          return true;
        }

        return false;
      });
    }, [searchText, allTimezones]);

    const handleMenuClick = (tz: TAugmentedTimezone) => {
      onSelect?.(tz.name);
      setOpen(false);
      setSearchText('');
    };

    const menu = (
      <div className="timezone-dropdown-menu w-[400px]">
        <div className="sticky top-0 bg-surface px-2 py-2 shadow-sm">
          <Input
            prefix={<SearchOutlined className="text-content-tertiary" />}
            placeholder={t('typeToSearchTimezone')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="mb-2"
          />
        </div>
        <Menu className="max-h-[300px] overflow-auto w-[400px]" onClick={() => {}}>
          {timezonesShowed.map((tz) => {
            const selected = value === tz.name;
            return (
              <Menu.Item
                key={tz.name}
                className={clsx(
                  'border-t border-default px-2 py-2 overflow-hidden',
                  selected && 'bg-overlay-hover',
                )}
                onClick={() => handleMenuClick(tz)}
              >
                <div className="grid grid-cols-[1fr,auto] items-center w-full gap-2">
                  <div className="overflow-hidden">
                    <div className="flex flex-col">
                      <span
                        className={clsx('truncate font-medium', selected && 'text-status-error')}
                      >
                        {tz.name}
                      </span>
                      <span className="truncate text-xs text-content-secondary">
                        {tz.countryNames.length > 0 ? tz.countryNames.join(', ') : ''}
                      </span>
                    </div>
                  </div>
                  <span
                    className={clsx(
                      'rounded px-2 py-1 whitespace-nowrap',
                      selected
                        ? 'bg-status-error-bg text-status-error'
                        : 'bg-surface-sunken text-content-tertiary',
                    )}
                  >
                    UTC{tz.utcOffsetStr}
                  </span>
                </div>
              </Menu.Item>
            );
          })}
        </Menu>
      </div>
    );

    return (
      <Dropdown
        overlay={menu}
        trigger={['click']}
        onVisibleChange={setOpen}
        visible={open}
        disabled={disabled}
        overlayClassName="timezone-dropdown-overlay"
      >
        <div
          data-id="timezone-dropdown"
          className={clsx(
            'flex justify-between rounded bg-surface-sunken px-2 py-2 w-full cursor-pointer text-content-primary',
            errors ? 'border-red-500 border-[1px]' : '',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
            className,
          )}
        >
          <div className={`flex w-full justify-between ${disabled ? 'text-content-tertiary' : ''}`}>
            <span>{value ?? t('selectTimezone')}</span>
            <DownOutlined className="mt-1" />
          </div>
        </div>
      </Dropdown>
    );
  },
);
